import { Box, ButtonGroup, Card, Grid, Icon, CircularProgress, Autocomplete, TextField, Chip, Menu, MenuItem, Tabs, styled, Tab, Stack, IconButton, Accordion, AccordionDetails, AccordionSummary, Divider } from "@mui/material";
import PropTypes from "prop-types";
import SoftBox from "components/SoftComponents/SoftBox";
import SoftButton from "components/SoftComponents/SoftButton";
import { Link, useNavigate } from "react-router-dom";
import SoftTypography from "components/SoftComponents/SoftTypography";
import { wrapper } from "utils/wrapper";
import { useRef, useState, useEffect } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { disableMap } from "utils/map";
import { useSoftUIController } from "hooks/context";
import { PhotoProvider, PhotoView } from "react-photo-view";
import SoftBadge from "components/SoftComponents/SoftBadge";
import SoftSelect from "components/SoftComponents/SoftSelect";
// import DOMPurify from 'dompurify';
import Swal from "sweetalert2";
import moment from "moment";

import campaignAPI from "services/api/campaign";
import sitesAPI from "services/api/sites";
import gtfsAPI from "services/api/gtfs";
import bksAPI from "services/api/bks";
import groupAPI from "services/api/groups";

import AddIcon from '@mui/icons-material/Add';

import { CampaignPreviewToggle } from "pages/components/Campaigns/CampaignsAll/CampaignPreview";

const FolderTab = styled(Tab, {
    shouldForwardProp: (prop) => prop !== 'tabColour' && prop !== 'active' && prop !== 'deviceLayout' && prop !== 'isVisible'
})(({ theme, tabColour, active, deviceLayout, isVisible }) => ({
    textTransform: 'none !important',
    justifyContent: deviceLayout !== 'mobile' ? 'center' : 'flex-start',
    fontSize: '0.95rem !important',
    color: '#344767 !important',
    minHeight: '48px !important',
    padding: '12px 16px !important',
    marginRight: '-16px !important',
    zIndex: active ? 2 : 1,
    borderRadius: '16px 20px 0 0 !important',
    transition: 'all 0.2s ease-in-out !important',
    border: '1px solid #e0e0e0',
    borderBottom: active ? '1px solid #fff !important' : '1px solid #e0e0e0',
    backgroundColor: active ? '#fff !important' : '#f5f5f5 !important',
    marginBottom: '-1px',
    position: 'relative', // Necessary for absolute positioning of pseudo-elements
    overflow: 'visible !important', // Allows the curves to "bleed" out the sides

    // --- The Inverse Curves ---
    '&::before, &::after': {
        content: active ? '""' : 'none', // Only show when tab is active
        position: 'absolute',
        bottom: 0,
        width: '20px', // Match this to your borderRadius (20px)
        height: '20px',
        backgroundColor: 'transparent',
        pointerEvents: 'none',
    },

    // Left Curve
    '&::before': {
        left: '-20px',
        borderBottomRightRadius: '20px',
        boxShadow: '5px 5px 0 5px #fff', // Bottom-right shadow creates the fill
    },

    // Right Curve
    '&::after': {
        right: '-20px',
        borderBottomLeftRadius: '20px',
        boxShadow: '-5px 5px 0 5px #fff', // Bottom-left shadow creates the fill
    },
    // --------------------------

    '&.Mui-selected': {
        color: '#334155 !important',
        fontWeight: '600 !important',
        width: deviceLayout === 'mobile' ? '100%' : 'unset',
    },

    [theme.breakpoints.down('xl')]: {
        display: isVisible ? 'inline-flex' : 'none !important',
    }
}));

// --- CAMPAIGN TAG FILTERING LOGIC ---
const doesCampaignMatchTags = (campaign, selectedTags) => {
    if (!selectedTags || selectedTags.length === 0) {
        return true;
    }

    const campaignTags = campaign.deviceFilter || [];
    if (campaignTags.length === 0) {
        return false;
    }

    return campaignTags.some(campTag =>
        selectedTags.some(uiTag =>
            uiTag.field === campTag.field && uiTag.value === campTag.value
        )
    );
};

// --- SCHEDULE FORMATTING HELPERS ---
const formatMinutes = (mins) => {
    if (typeof mins !== 'number') return '';
    const h = Math.floor(mins / 60).toString().padStart(2, '0');
    const m = (mins % 60).toString().padStart(2, '0');
    return `${h}:${m}`;
};

const formatDays = (daysObj) => {
    if (!daysObj) return '';
    const activeDays = Object.keys(daysObj).filter(d => daysObj[d]);
    if (activeDays.length === 7) return 'Everyday';
    if (activeDays.length === 0) return 'No days set';
    const dayMap = { monday: 'Mon', tuesday: 'Tue', wednesday: 'Wed', thursday: 'Thu', friday: 'Fri', saturday: 'Sat', sunday: 'Sun' };
    return activeDays.map(d => dayMap[d]).join(', ');
};

const getScheduleSummary = (campaign, organisation) => {
    if (!campaign || !campaign.scheduleType) return "Not Scheduled";

    if (campaign.scheduleType === 'alwaysOn') return "Always On";

    if (campaign.scheduleType === 'startEnd') {
        const slots = campaign.scheduling?.startEnd || [];
        if (!slots.length) return "No dates configured";
        const first = slots[0];
        const start = moment(first.startTime).format('MMM D, HH:mm');
        const end = moment(first.endTime).format('MMM D, HH:mm');
        return `${start} - ${end}${slots.length > 1 ? ` (+${slots.length - 1})` : ''}`;
    }

    if (campaign.scheduleType === 'recurring') {
        const slots = campaign.scheduling?.recurring || [];
        if (!slots.length) return "No times configured";
        const first = slots[0];
        const daysStr = formatDays(first.days);
        const timeStr = `${formatMinutes(first.startTime)} - ${formatMinutes(first.endTime)}`;
        return `${daysStr} • ${timeStr}${slots.length > 1 ? ` (+${slots.length - 1})` : ''}`;
    }

    if (campaign.scheduleType === 'recurringWithDate') {
        const slots = campaign.scheduling?.recurringWithDate || [];
        if (!slots.length) return "No times configured";
        const first = slots[0];
        const daysStr = formatDays(first.days);
        const timeStr = `${formatMinutes(first.startTime)} - ${formatMinutes(first.endTime)}`;
        const dateStr = `${moment(first.startDate).utc().format('MMM D')} - ${moment(first.endDate).utc().format('MMM D')}`;
        return `${daysStr} • ${timeStr} (${dateStr})${slots.length > 1 ? ` (+${slots.length - 1})` : ''}`;
    }

    return "Scheduled";
};

// --- ACTIVE RIGHT NOW LOGIC ---
const checkIfPlayingNow = (campaign, organisation) => {
    if (!campaign.enabled) return false;
    if (campaign.scheduleType === 'alwaysOn') return true;

    const now = moment();
    const currentMins = now.hours() * 60 + now.minutes();
    const currentDay = now.format('dddd').toLowerCase();

    if (campaign.scheduleType === 'startEnd') {
        const slots = campaign.scheduling?.startEnd || [];
        return slots.some(slot => {
            return now.isBetween(moment(slot.startTime), moment(slot.endTime))
        });
    }

    if (campaign.scheduleType === 'recurring') {
        const slots = campaign.scheduling?.recurring || [];
        return slots.some(slot =>
            slot.days?.[currentDay] && currentMins >= slot.startTime && currentMins <= slot.endTime
        );
    }

    if (campaign.scheduleType === 'recurringWithDate') {
        const slots = campaign.scheduling?.recurringWithDate || [];
        return slots.some(slot => {
            const withinDates = now.isSameOrAfter(moment(slot.startDate).utc().startOf('day')) &&
                now.isSameOrBefore(moment(slot.endDate).utc().endOf('day'));
            const withinTime = currentMins >= slot.startTime && currentMins <= slot.endTime;
            const correctDay = slot.days?.[currentDay];
            return withinDates && withinTime && correctDay;
        });
    }

    return false;
};

const CampaignContentItem = ({ opt, appearance, clickState, organisation, campaignType, handleToggleStatus, handleDelete, toggleStatusMutation, deleteCampaignMutation, setViewedCampaign, canCampaignsEdit, canCampaignsDelete }) => (
    <PhotoProvider>
        <Card
            sx={({ palette: { white }, functions: { rgba } }) => ({
                height: '100%',
                background: opt.enabled ? '#fff !important' : "#eee !important",
                boxShadow: checkIfPlayingNow(opt, organisation) ? `0 0 15px 2px ${rgba(campaignType?.colour || '#fff', 0.4)}` : "none",
                border: `1px solid ${rgba(campaignType?.colour || '#fff', 0.5)}`
            })}
        >
            <SoftBox p={2} height="100%" display="flex" flexDirection="column" justifyContent="space-between">
                <SoftBox height="100%" display="flex" justifyContent="center" alignItems="center">
                    <CampaignPreviewToggle {...{ opt, campaignType }} selectedOrg={organisation.organisation_id} />
                </SoftBox>
                <SoftBox>
                    <SoftBox width="100%" pb={1} display="flex" justifyContent="space-between" gap={1}>
                        {checkIfPlayingNow(opt, organisation) ? (
                            <SoftBadge
                                variant="gradient"
                                color="dark"
                                badgeContent="▶ Live Now"
                                container
                            />
                        ) : (
                            <SoftBadge
                                variant="gradient"
                                color="light"
                                badgeContent="Planned"
                                container
                                sx={{ opacity: 0, cursor: 'default' }}
                            />
                        )}
                    </SoftBox>
                    <SoftBox p={1.5} pb={1} display="flex" flexDirection='column' sx={{ borderRadius: '10px', backgroundColor: '#e6e6e6' }}>
                        <SoftTypography variant="button" sx={{ fontSize: '18px' }}>{opt.name}</SoftTypography>
                        <SoftTypography variant="caption" sx={{ fontSize: '13px', lineHeight: '1.5', mt: 0.5, color: '#666', display: 'flex', alignItems: 'center' }}>
                            <Icon sx={{ fontSize: '14px', mr: 0.5 }}>schedule</Icon>
                            {getScheduleSummary(opt, organisation)}
                        </SoftTypography>
                    </SoftBox>
                    <SoftBox pt={1} pb={2}><Divider /></SoftBox>
                    <SoftBox>
                        {(canCampaignsEdit || canCampaignsDelete) ? (
                            <Grid container spacing={1}>
                                {canCampaignsEdit && (
                                    <>
                                        <Grid item xxs={12} xs={4}>
                                            <SoftButton variant="outlined" color={appearance} size="small" fullWidth onClick={() => setViewedCampaign(opt._id)}>
                                                <Icon sx={{ paddingBottom: '3px', paddingRight: '5px' }}>settings</Icon> Edit
                                            </SoftButton>
                                        </Grid>
                                        <Grid item xxs={12} xs={4}>
                                            <SoftButton
                                                variant="outlined"
                                                color={opt.enabled ? appearance : "enable"}
                                                size="small"
                                                fullWidth
                                                onClick={() => handleToggleStatus(opt._id)}
                                                disabled={toggleStatusMutation.isPending}
                                            >
                                                <Icon sx={{ paddingBottom: '3px', paddingRight: '5px' }}>power_settings_new</Icon> {opt.enabled ? "Disable" : "Enable"}
                                            </SoftButton>
                                        </Grid>
                                    </>
                                )}
                                {canCampaignsDelete && (
                                    <Grid item xxs={12} xs={4}>
                                        <SoftButton
                                            variant="outlined"
                                            color={clickState == opt._id ? "error" : appearance}
                                            size="small"
                                            fullWidth
                                            onClick={() => handleDelete(opt)}
                                            disabled={deleteCampaignMutation.isPending}
                                        >
                                            <Icon sx={{ paddingBottom: '3px', paddingRight: '5px' }}>delete</Icon> {clickState == opt._id ? "Confirm" : "Delete"}
                                        </SoftButton>
                                    </Grid>
                                )}
                            </Grid>
                        ) : (
                            <Grid container spacing={1}>
                                <Grid item xxs={6}>
                                    <SoftButton variant="outlined" color={appearance} size="small" fullWidth onClick={() => setViewedCampaign(opt._id)}>
                                        <Icon sx={{ paddingBottom: '3px', paddingRight: '5px' }}>launch</Icon> View
                                    </SoftButton>
                                </Grid>
                                <Grid item xxs={6}>
                                    <SoftButton
                                        variant="outlined"
                                        color={opt.enabled ? "enable" : appearance}
                                        size="small"
                                        fullWidth
                                        disabled
                                    >
                                        <Icon sx={{ paddingBottom: '3px', paddingRight: '5px' }}>power_settings_new</Icon> {opt.enabled ? "Enabled" : "Disabled"}
                                    </SoftButton>
                                </Grid>
                            </Grid>
                        )}
                    </SoftBox>
                </SoftBox>
            </SoftBox>
        </Card>
    </PhotoProvider>
);

const LazyCampaignCard = (props) => {
    const [placeholder, setPlaceholder] = useState(true);
    const containerRef = useRef(null);

    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setPlaceholder(false);
                    observer.disconnect(); // Stop watching once loaded
                }
            },
            {
                rootMargin: '200px', // Starts loading 200px before coming into view
            }
        );

        if (containerRef.current) {
            observer.observe(containerRef.current);
        }

        return () => observer.disconnect();
    }, []);

    return (
        <div ref={containerRef} style={{ height: '100%', minHeight: '250px' }}>
            {placeholder ? (
                // A minimal skeleton matching your card's background shape
                <div style={{ height: '100%', background: '#eee', borderRadius: '12px' }} />
            ) : (
                <CampaignContentItem {...props} />
            )}
        </div>
    );
};

function AllCampaignsView({ map, user, initialTags = [], isEmbedded = false }) {
    const [controller] = useSoftUIController();
    const { deviceLayout, selectedOrg, userPermissions, appearance } = controller;

    useEffect(() => {
        if (!map.current) return;
        disableMap(map, user, selectedOrg);
    }, [selectedOrg]);

    campaignAPI.loadToken(useAuth0());
    sitesAPI.loadToken(useAuth0());
    gtfsAPI.loadToken(useAuth0());
    bksAPI.loadToken(useAuth0());
    groupAPI.loadToken(useAuth0());

    const queryClient = useQueryClient();
    const navigate = useNavigate();

    const canCampaignsAdd = userPermissions.indexOf('campaign_manager:add') >= 0;
    const canCampaignsEdit = userPermissions.indexOf('campaign_manager:edit') >= 0;
    const canCampaignsDelete = userPermissions.indexOf('campaign_manager:delete') >= 0;

    const canViewMedia = userPermissions.indexOf('media') >= 0;

    const [filtersExpanded, setFiltersExpanded] = useState(isEmbedded);
    const [selectedTabId, setSelectedTabId] = useState(0);
    const [tags, setTags] = useState(initialTags);
    const [campaignFilter, setCampaignFilter] = useState({ value: 1, label: "All" });

    const [clickState, setClickState] = useState(null);

    const [anchorEl, setAnchorEl] = useState(null);
    const openMenu = Boolean(anchorEl);
    const handleOpenMenu = (event) => setAnchorEl(event.currentTarget);
    const handleCloseMenu = () => setAnchorEl(null);

    const setViewedCampaign = (id) => navigate(`/campaigns/${id}`);

    const campaigns = useQuery({
        queryKey: ['campaigns', selectedOrg],
        queryFn: () => campaignAPI.getAllCampaigns(),
        staleTime: 5 * 60 * 1000,
    });

    const campaignTypes = useQuery({
        queryKey: ['campaignTypes', selectedOrg],
        queryFn: () => campaignAPI.getCampaignTypes(),
        staleTime: 5 * 60 * 1000,
    });

    const deviceGroups = useQuery({
        queryKey: ['getAllGroups', selectedOrg],
        queryFn: async () => {
            return await groupAPI.getAllGroups();
        }
    });

    const available_tags = useQuery({
        queryKey: ['getAssignedTags', selectedOrg],
        queryFn: async () => {
            return await gtfsAPI.getAssignedTags();
        }
    });

    // --- MUTATIONS ---
    const toggleStatusMutation = useMutation({
        mutationFn: (id) => campaignAPI.toggleCampaignStatus(id),
        onSuccess: (updatedCampaign) => {
            queryClient.setQueryData(['campaigns'], (oldCampaigns) => {
                if (!oldCampaigns) return [];
                return oldCampaigns.map(campaign =>
                    campaign._id === updatedCampaign._id ? updatedCampaign : campaign
                );
            });
            queryClient.invalidateQueries({ queryKey: ['campaigns', selectedOrg] });
        },
        onError: (error) => {
            console.error("Failed to toggle campaign status:", error);
            Swal.fire(`<div style="font-family:Roboto,Helvetica,Arial,sans-serif; font-weight:500; font-size:28px">Error</div>`, `<div style="font-family:Roboto,Helvetica,Arial,sans-serif; font-weight:500; font-size:20px">Could not update campaign status.</div>`, "error");
        }
    });

    const deleteCampaignMutation = useMutation({
        mutationFn: (id) => campaignAPI.deleteCampaign(id),
        onSuccess: () => {
            Swal.fire({
                icon: 'success',
                title: '<div style="font-family:Roboto,Helvetica,Arial,sans-serif; font-weight:500; font-size:16px">Deleted!</div>',
                toast: true,
                position: 'top-end',
                showConfirmButton: false,
                timer: 3000
            });
            queryClient.invalidateQueries({ queryKey: ['campaigns', selectedOrg] });
            setClickState(null);
        }
    });

    // --- HANDLERS ---
    const handleToggleStatus = (id) => {
        toggleStatusMutation.mutate(id);
    };

    const handleDelete = (campaign) => {
        if (clickState == campaign._id) {
            deleteCampaignMutation.mutate(campaign._id);
        }
        else {
            setClickState(campaign._id);
        }
    };

    const handleAddCampaign = (campaignType) => {
        navigate(`/campaigns/new?typeId=${campaignType._id}`);
    };

    const handleChange = (event, newValue) => {
        setSelectedTabId(newValue);
    };

    // GROUPS ADDITION
    var transport_options = [
        {category: 'Group', field: 'group_id', value: 'all_org_sites', id: 'all_org_sites', label: 'All Sites'},
        ...(deviceGroups.data || []).filter(dg => dg.type == "campaigns").map(dg => ({
            category: 'Group', field: 'group_id', value: dg.group_id, id: dg.group_id, label: dg.name
        })),
        ...(available_tags.data?.stops || []).map((r) => ({
            category: r?.creation_type?.indexOf('gtfs') > 0 ? 'Stop' : 'Site', field: 'stop_id', value: r.site_id, id: r.site_id, label: (r?.site_name ? `${r.stop_id} - ${r.site_name}` : `${r.stop_id}`)
        })),
        ...(available_tags.data?.routes || []).map((route) => ({
            category: 'Route', field: 'route_id', value: route.route_short_name, label: `Route ${route.route_short_name} - ${route.route_long_name}`
        }))
    ];

    const tabList = [
        { label: "All", _id: "all", colour: "#344767" },
        ...(campaignTypes.data || [])
    ];

    const filter_options = [
        { value: 1, label: "All" },
        { value: 2, label: "Enabled" },
        { value: 3, label: "Disabled" }
    ];

    const current_organisation = user.organisations.find(org => org.organisation_id === selectedOrg);

    const currentCampaigns = (
        <SoftBox display="flex" flexDirection="column" borderRadius="10px" color={"white"} width="100%" height={isEmbedded ? (deviceLayout == 'mobile' ? "calc(100svh - 8.5rem - 16px)" : "calc(100svh - 10.25rem - 16px)") : "100%"}>
            <SoftBox height="100%" sx={({ functions: { rgba } }) => ({ borderRadius: '16px 16px 0 0' })}>
                <SoftBox px={isEmbedded ? 0 : 1} height="100%" display="flex" flexDirection="column">
                    <SoftBox py={1} px={2} sx={({ palette, functions: { rgba } }) => ({ borderRadius: '16px 16px 0 0', backgroundColor: rgba(palette[appearance].header, 1), borderBottom: `1px solid ${rgba(palette[appearance].border, 1)}` })}>
                        <Grid container spacing={1}>
                            <Grid item xxs={canViewMedia ? 7 : 12} sx={{ display: { xxs: 'block', xl: 'none' } }}>
                                <SoftBox display="flex" flexDirection="column">
                                    <SoftBox mb={0.5} pl={0.5}>
                                        <SoftTypography variant="caption" fontWeight="bold">Campaign Status</SoftTypography>
                                    </SoftBox>
                                    <SoftBox sx={{ borderRadius: '10px', background: '#fff', width: '100%', zIndex: 100 }}>
                                        <SoftSelect size="medium" sx={{ height: 'calc(100% - 4px) !important' }} value={campaignFilter} options={filter_options} onChange={setCampaignFilter} />
                                    </SoftBox>
                                </SoftBox>
                            </Grid>
                            {canViewMedia && (
                                <Grid item xxs={5} sx={{ display: { xxs: 'block', xl: 'none' } }}>
                                    <SoftBox height="100%" display="flex" alignItems="flex-end">
                                        <SoftButton component={Link} to="/media" variant="gradient" color="dark" fullWidth>View Media</SoftButton>
                                    </SoftBox>
                                </Grid>
                            )}
                            <Grid item xxs={12} xl={7}>
                                <SoftBox display="flex" flexDirection="column" mr={{ xxs: 0, xl: 0 }}>
                                    <SoftBox mb={0.5} pl={0.5}>
                                        <SoftTypography variant="caption" fontWeight="bold">Tags</SoftTypography>
                                    </SoftBox>
                                    <SoftBox width="100%">
                                        <Autocomplete
                                            multiple
                                            id="transport-targets"
                                            options={transport_options}
                                            groupBy={(option) => option.category}
                                            getOptionLabel={(option) => option.label}
                                            isOptionEqualToValue={(option, val) => option.field === val.field && option.value === val.value}
                                            value={tags || []}
                                            onChange={(event, newValue) => { setTags(newValue); }}
                                            renderTags={(tagValue, getTagProps) =>
                                                tagValue.map((option, index) => {
                                                    const { key, ...tagProps } = getTagProps({ index });
                                                    return (
                                                        <Chip
                                                            key={key}
                                                            variant="outlined"
                                                            label={`${option.category}: ${option.label || option.value}`}
                                                            {...tagProps}
                                                            sx={{
                                                                transition: 'none !important',
                                                                '& .MuiChip-label': { transition: 'none !important' }
                                                            }}
                                                        />
                                                    );
                                                })
                                            }
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    variant="outlined"
                                                    placeholder={tags.length > 0 ? "" : "Select groups, routes, stops, or devices..."}
                                                    sx={{
                                                        "& .MuiOutlinedInput-root": {
                                                            padding: `0.${tags.length ? 2 : 5}rem 0.75rem 0.${tags.length ? 2 : 5}rem 0.75rem !important`,
                                                            transition: 'none !important',
                                                        }
                                                    }}
                                                />
                                            )}
                                        />
                                    </SoftBox>
                                </SoftBox>
                            </Grid>
                            <Grid item xxs={canViewMedia ? 2.5 : 5} sx={{ display: { xxs: 'none', xl: 'block' } }}>
                                <SoftBox display="flex" flexDirection="column">
                                    <SoftBox mb={0.5} pl={0.5}>
                                        <SoftTypography variant="caption" fontWeight="bold">Campaign Status</SoftTypography>
                                    </SoftBox>
                                    <SoftBox sx={{ borderRadius: '10px', background: '#fff', width: '100%', zIndex: 100 }}>
                                        <SoftSelect size="medium" sx={{ height: 'calc(100% - 4px) !important' }} value={campaignFilter} options={filter_options} onChange={setCampaignFilter} />
                                    </SoftBox>
                                </SoftBox>
                            </Grid>
                            {canViewMedia && (
                                <Grid item xxs={2.5} sx={{ display: { xxs: 'none', xl: 'block' } }}>
                                    <SoftBox height="100%" display="flex" alignItems="flex-start" pt={'21px'}>
                                        <SoftButton component={Link} to="/media" variant="gradient" color="dark" fullWidth>View Media</SoftButton>
                                    </SoftBox>
                                </Grid>
                            )}
                        </Grid>
                    </SoftBox>
                    <SoftBox
                        display="flex"
                        flexDirection="column"
                        flex={1}
                        minHeight={0}
                        sx={({ palette, functions: { rgba } }) => ({ p: 1, backgroundColor: palette[appearance].content, borderRadius: "0 0 16px 16px" })}
                    >
                        <SoftBox display="flex" flexDirection="column" height="100%">
                            <SoftBox>
                                <Tabs
                                    value={selectedTabId}
                                    onChange={handleChange}
                                    TabIndicatorProps={{ style: { display: 'none' } }}
                                    variant="scrollable"
                                    scrollButtons={false}
                                    sx={{
                                        minHeight: '48px', width: '100%', padding: 0, borderRadius: 0, background: 'transparent',
                                        '& .MuiTabs-flexContainer': {
                                            width: 'calc(100% - 14px)'
                                        },
                                        '& .MuiTabs-scroller': {
                                            overflow: 'visible !important',
                                            display: 'flex',
                                            justifyContent: { xxs: 'flex-start' },
                                            padding: { xxs: 0 }
                                        }
                                    }}
                                >
                                    {tabList.map((tab, tab_index) => {
                                        const isVisible = Math.abs(tab_index - selectedTabId) <= 1;

                                        return (
                                            <FolderTab
                                                key={tab_index}
                                                value={tab_index}
                                                tabColour={tab?.colour || '#000'}
                                                active={selectedTabId === tab_index}
                                                deviceLayout={deviceLayout}
                                                isVisible={isVisible}
                                                component={"div"}
                                                label={
                                                    <>
                                                        {deviceLayout != 'mobile' ? (
                                                            <>
                                                                <SoftBox position="absolute" left={10} top='auto'>
                                                                    {tab_index !== 0 && (
                                                                        <>
                                                                            {canCampaignsAdd ? (
                                                                                <IconButton
                                                                                    size="small"
                                                                                    onClick={(e) => {
                                                                                        e.stopPropagation();
                                                                                        if (tab_index == selectedTabId) {
                                                                                            // console.log("Add button clicked for:", tab.label);
                                                                                            handleAddCampaign(tab)
                                                                                        }
                                                                                    }}
                                                                                    sx={{
                                                                                        width: tab_index === selectedTabId ? '1.75rem' : '0.75rem',
                                                                                        height: tab_index === selectedTabId ? '1.75rem' : '0.75rem',
                                                                                        backgroundColor: tab?.colour || '#000',
                                                                                        color: '#fff',
                                                                                        '&:hover': {
                                                                                            backgroundColor: tab?.colour || '#000',
                                                                                            filter: tab_index == selectedTabId && 'brightness(0.9)',
                                                                                            transform: tab_index == selectedTabId && 'scale(1.1)',
                                                                                        },
                                                                                        boxShadow: tab_index === selectedTabId ? `0 0 8px ${tab?.colour || '#000'}` : 'none',
                                                                                        transition: 'all 0.2s ease',
                                                                                    }}
                                                                                >
                                                                                    {tab_index === selectedTabId && (
                                                                                        <AddIcon sx={{ fontSize: '1.5rem !important', color: `${'#fff'} !important`, marginRight: '0 !important' }} />
                                                                                    )}
                                                                                </IconButton>
                                                                            ) : (
                                                                                <IconButton
                                                                                    size="small"
                                                                                    sx={{
                                                                                        width: '0.75rem',
                                                                                        height: '0.75rem',
                                                                                        backgroundColor: tab?.colour || '#000',
                                                                                        color: '#fff',
                                                                                        '&:hover': {
                                                                                            backgroundColor: tab?.colour || '#000'
                                                                                        }
                                                                                    }}
                                                                                />
                                                                            )}
                                                                        </>
                                                                    )}
                                                                </SoftBox>
                                                                <SoftTypography variant="inherit">{tab.label}</SoftTypography>
                                                            </>
                                                        ) : (
                                                            <SoftBox display="flex" flexDirection="row" alignItems="center" justifyContent="flex-start">
                                                                <SoftBox pr={2}>
                                                                    {tab_index !== 0 && (
                                                                        <>
                                                                            {canCampaignsAdd ? (
                                                                                <IconButton
                                                                                    size="small"
                                                                                    onClick={(e) => {
                                                                                        e.stopPropagation();
                                                                                        if (tab_index == selectedTabId) {
                                                                                            // console.log("Add button clicked for:", tab.label);
                                                                                            handleAddCampaign(tab)
                                                                                        }
                                                                                    }}
                                                                                    sx={{
                                                                                        width: tab_index === selectedTabId ? '1.75rem' : '0.75rem',
                                                                                        height: tab_index === selectedTabId ? '1.75rem' : '0.75rem',
                                                                                        backgroundColor: tab?.colour || '#000',
                                                                                        color: '#fff',
                                                                                        '&:hover': {
                                                                                            backgroundColor: tab?.colour || '#000',
                                                                                            filter: tab_index == selectedTabId && 'brightness(0.9)',
                                                                                            transform: tab_index == selectedTabId && 'scale(1.1)',
                                                                                        },
                                                                                        boxShadow: tab_index === selectedTabId ? `0 0 8px ${tab?.colour || '#000'}` : 'none',
                                                                                        transition: 'all 0.2s ease',
                                                                                    }}
                                                                                >
                                                                                    {tab_index === selectedTabId && (
                                                                                        <AddIcon sx={{ fontSize: '1.5rem !important', color: `${'#fff'} !important`, marginRight: '0 !important' }} />
                                                                                    )}
                                                                                </IconButton>
                                                                            ) : (
                                                                                <IconButton
                                                                                    size="small"
                                                                                    sx={{
                                                                                        width: '0.75rem',
                                                                                        height: '0.75rem',
                                                                                        backgroundColor: tab?.colour || '#000',
                                                                                        color: '#fff',
                                                                                        '&:hover': {
                                                                                            backgroundColor: tab?.colour || '#000',
                                                                                        },
                                                                                    }}
                                                                                />
                                                                            )}
                                                                        </>
                                                                    )}
                                                                </SoftBox>
                                                                <SoftTypography variant="inherit">{tab.label}</SoftTypography>
                                                            </SoftBox>
                                                        )}
                                                    </>
                                                }
                                            />
                                        )
                                    })}
                                </Tabs>
                            </SoftBox>
                            <SoftBox
                                flex={1}
                                sx={{
                                    backgroundColor: "#fff",
                                    borderRadius: `${selectedTabId == 0 ? '0' : '16px'} ${selectedTabId == tabList.length - 1 ? '0' : '16px'} 16px 16px`,
                                    mt: "-1px",
                                    overflowY: "auto",
                                    p: 3
                                }}
                            >
                                <Grid container spacing={3}>
                                    {campaignTypes?.data && (() => {
                                        const activeTab = tabList[selectedTabId];
                                        const filtered = campaigns.data?.filter(c => {
                                            const matchesFolder = selectedTabId === 0 || c.campaignTypeId === activeTab._id;
                                            const matchesStatus = campaignFilter.value === 1 ||
                                                (campaignFilter.value === 2 && c.enabled) ||
                                                (campaignFilter.value === 3 && !c.enabled);
                                            return matchesFolder && matchesStatus && doesCampaignMatchTags(c, tags);
                                        }) || [];

                                        if (filtered.length === 0) {
                                            return (
                                                <SoftBox display="flex" justifyContent={{ xxs: 'center', lg: "flex-start" }} pl={2} pt={2}>
                                                    <SoftTypography variant="body3" color="secondary" fontWeight="regular" sx={{ fontStyle: 'italic' }}>
                                                        No campaigns found.
                                                    </SoftTypography>
                                                </SoftBox>
                                            );
                                        }

                                        return filtered.map((opt, index) => (
                                            <Grid item key={opt._id || index} xxs={12} md={6} lg={4} xxl={3} x5l={2}>
                                                <LazyCampaignCard opt={opt} clickState={clickState} appearance={appearance} organisation={current_organisation} campaignType={campaignTypes.data?.find(t => t._id === opt.campaignTypeId)}
                                                    {...{ handleToggleStatus, handleDelete, toggleStatusMutation, deleteCampaignMutation, setViewedCampaign, canCampaignsEdit, canCampaignsDelete }} />
                                            </Grid>
                                        ));
                                    })()}
                                </Grid>
                            </SoftBox>
                        </SoftBox>
                    </SoftBox>
                </SoftBox>
            </SoftBox>
        </SoftBox>
    );

    if (isEmbedded) return currentCampaigns;
    return wrapper((
        <SoftBox borderRadius="20px" color={"white"} width="100%" height={deviceLayout == 'mobile' ? 'calc(100svh - 4.25rem - 8px)' : "calc(100svh - 5.5rem - 16px)"} minHeight={deviceLayout == 'mobile' && "calc(100svh - 4.25rem - 8px)"}
            sx={({ palette, functions: { rgba } }) => ({ backgroundColor: rgba(palette[appearance].background, 0.7), overflowY: 'hidden' })}
        >
            <SoftBox mt={1} display="flex" sx={{ height: deviceLayout == 'mobile' ? `calc(100% - 16px)` : `calc(100% - 16px)` }}>
                {currentCampaigns}
            </SoftBox>
        </SoftBox>
    ), controller);
}

AllCampaignsView.propTypes = {
    map: PropTypes.object,
    user: PropTypes.object,
    isEmbedded: PropTypes.bool
}

export default AllCampaignsView;